import { createContext, useContext, useEffect, useMemo, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from './auth';

const ACTIVE_CLINIC_STORAGE_KEY = 'clinio_active_clinic_id';

export interface ClinicInfo {
  id: string;
  name: string;
  role: 'main_clinic_admin' | 'clinic_admin' | 'employee';
  parentClinicId: string | null;
  isSubClinic: boolean;
  isActive?: boolean; // For sub-clinics: whether they are individually active
  isAccessible?: boolean; // Whether the clinic is accessible (parent active + sub-clinic active)
}

interface ActiveClinicContextValue {
  activeClinicId: string | null;
  activeClinic: ClinicInfo | null;
  availableClinics: ClinicInfo[];
  isMainClinicAdmin: boolean;
  hasMultipleClinics: boolean;
  subClinics: ClinicInfo[];
  setActiveClinicId: (clinicId: string) => void;
  loading: boolean;
}

const ActiveClinicContext = createContext<ActiveClinicContextValue | undefined>(undefined);

export function ActiveClinicProvider({ children }: { children: ReactNode }) {
  const { memberships, status, claims } = useAuth();
  const [activeClinicId, setActiveClinicIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_CLINIC_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Build available clinics from JWT memberships
  const availableClinics = useMemo<ClinicInfo[]>(() => {
    return memberships.map(m => ({
      id: m.clinic_id,
      name: m.clinic_name ?? 'Unknown Clinic',
      role: m.role as ClinicInfo['role'],
      parentClinicId: m.parent_clinic_id ?? null,
      isSubClinic: m.parent_clinic_id != null,
      isActive: m.is_active ?? true, // Default to true for backward compatibility
      isAccessible: m.is_accessible ?? true, // Default to true for backward compatibility
    }));
  }, [memberships]);

  // Get main clinics (no parent)
  const mainClinics = useMemo(() => 
    availableClinics.filter(c => !c.isSubClinic), 
    [availableClinics]
  );

  // Get sub-clinics of the active clinic (if it's a main clinic)
  const subClinics = useMemo(() => {
    if (!activeClinicId) return [];
    return availableClinics.filter(c => c.parentClinicId === activeClinicId);
  }, [availableClinics, activeClinicId]);

  // Active clinic object
  const activeClinic = useMemo(() => 
    availableClinics.find(c => c.id === activeClinicId) ?? null,
    [availableClinics, activeClinicId]
  );

  // Is current user main_clinic_admin of active clinic?
  const isMainClinicAdmin = useMemo(() => 
    activeClinic?.role === 'main_clinic_admin',
    [activeClinic]
  );

  // Set active clinic with persistence
  const setActiveClinicId = useCallback((clinicId: string) => {
    try {
      localStorage.setItem(ACTIVE_CLINIC_STORAGE_KEY, clinicId);
    } catch {
      // localStorage not available
    }
    setActiveClinicIdState(clinicId);
  }, []);

  // Auto-select clinic on load or when memberships change
  useEffect(() => {
    if (status !== 'authenticated') return;

    // Drop stale localStorage clinic id when JWT has no memberships yet.
    if (availableClinics.length === 0) {
      if (activeClinicId !== null) {
        setActiveClinicIdState(null);
        try {
          localStorage.removeItem(ACTIVE_CLINIC_STORAGE_KEY);
        } catch {
          // localStorage not available
        }
      }
      return;
    }

    // Clear stale localStorage if current value is not in JWT memberships
    if (activeClinicId && !availableClinics.some(c => c.id === activeClinicId)) {
      console.warn(`[ActiveClinic] Clearing stale clinic ID ${activeClinicId} - not in JWT memberships`);
      setActiveClinicIdState(null);
      try {
        localStorage.removeItem(ACTIVE_CLINIC_STORAGE_KEY);
      } catch {
        // localStorage not available
      }
      // Fall through to select a new clinic below
    }

    // If saved clinic is still valid, keep it
    if (activeClinicId && availableClinics.some(c => c.id === activeClinicId)) {
      return;
    }
    
    // Otherwise select primary or first available
    const primary = claims.primary_clinic_id;
    if (primary && availableClinics.some(c => c.id === primary)) {
      setActiveClinicId(primary);
    } else if (mainClinics.length > 0) {
      setActiveClinicId(mainClinics[0].id);
    } else if (availableClinics.length > 0) {
      setActiveClinicId(availableClinics[0].id);
    }
  }, [status, availableClinics, activeClinicId, claims.primary_clinic_id, mainClinics, setActiveClinicId]);

  // Validate that activeClinicId exists in availableClinics
  // If not, return null to prevent using stale localStorage value
  const validatedActiveClinicId = useMemo(() => {
    if (!activeClinicId || availableClinics.length === 0) return activeClinicId;
    return availableClinics.some(c => c.id === activeClinicId) ? activeClinicId : null;
  }, [activeClinicId, availableClinics]);

  const validatedActiveClinic = useMemo(() => 
    availableClinics.find(c => c.id === validatedActiveClinicId) ?? null,
    [availableClinics, validatedActiveClinicId]
  );

  const value = useMemo<ActiveClinicContextValue>(() => ({
    activeClinicId: validatedActiveClinicId,
    activeClinic: validatedActiveClinic,
    availableClinics,
    isMainClinicAdmin,
    hasMultipleClinics: availableClinics.length > 1,
    subClinics,
    setActiveClinicId,
    loading: status === 'initializing',
  }), [validatedActiveClinicId, validatedActiveClinic, availableClinics, isMainClinicAdmin, subClinics, setActiveClinicId, status]);

  return (
    <ActiveClinicContext.Provider value={value}>
      {children}
    </ActiveClinicContext.Provider>
  );
}

export function useActiveClinic() {
  const context = useContext(ActiveClinicContext);
  if (!context) {
    throw new Error('useActiveClinic must be used within ActiveClinicProvider');
  }
  return context;
}
